import React, {useCallback, useEffect, useRef, useState} from 'react'
import {AppState, type AppStateStatus} from 'react-native'
import {type MatrixClient} from 'matrix-js-sdk'

import {logger} from '#/logger'
import {useSession} from '#/state/session'
import {createMatrixClient, type MatrixCredentials} from './client'
import {pendingMessageStore} from './pendingMessages'
import {
  INACTIVE_TIMEOUT,
  isMatrixReady,
  type MatrixConnectionError,
  MatrixConnectionStatus,
  MatrixErrorCode,
} from './types'

type MatrixState = {
  client: MatrixClient | null
  status: MatrixConnectionStatus
  error: MatrixConnectionError | null
  /** @deprecated Use isMatrixReady(status) instead */
  isConnected: boolean
  /** @deprecated Use status !== Uninitialized instead */
  isSyncing: boolean
}

const MatrixContext = React.createContext<MatrixState>({
  client: null,
  status: MatrixConnectionStatus.Uninitialized,
  error: null,
  isConnected: false,
  isSyncing: false,
})

export function useMatrixContext() {
  return React.useContext(MatrixContext)
}

export function MatrixProvider({children}: {children: React.ReactNode}) {
  const {currentAccount} = useSession()

  const hasCredentials =
    currentAccount?.matrixAccessToken &&
    currentAccount?.matrixUserId &&
    currentAccount?.matrixDeviceId &&
    currentAccount?.matrixHomeserver

  if (!hasCredentials) {
    return (
      <MatrixContext.Provider
        value={{
          client: null,
          status: MatrixConnectionStatus.Uninitialized,
          error: null,
          isConnected: false,
          isSyncing: false,
        }}>
        {children}
      </MatrixContext.Provider>
    )
  }

  return (
    <MatrixProviderInner
      credentials={{
        matrixUserId: currentAccount.matrixUserId!,
        matrixAccessToken: currentAccount.matrixAccessToken!,
        matrixDeviceId: currentAccount.matrixDeviceId!,
        matrixHomeserver: currentAccount.matrixHomeserver!,
      }}>
      {children}
    </MatrixProviderInner>
  )
}

function MatrixProviderInner({
  children,
  credentials,
}: {
  children: React.ReactNode
  credentials: MatrixCredentials
}) {
  const [client, setClient] = useState<MatrixClient | null>(null)
  const [status, setStatus] = useState<MatrixConnectionStatus>(
    MatrixConnectionStatus.Uninitialized,
  )
  const [error, setError] = useState<MatrixConnectionError | null>(null)
  const appStateRef = useRef<AppStateStatus>('active')
  const clientRef = useRef<MatrixClient | null>(null)
  const pruneIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const backgroundedAtRef = useRef<number | null>(null)

  const startSync = useCallback((c: MatrixClient) => {
    setStatus(MatrixConnectionStatus.Syncing)
    setError(null)
    try {
      c.startClient({initialSyncLimit: 20})
    } catch (e) {
      logger.error('Failed to start Matrix client', {safeMessage: e})
      setStatus(MatrixConnectionStatus.Error)
      setError({
        code: MatrixErrorCode.InitFailed,
        exception: e instanceof Error ? e : new Error(String(e)),
        retry: () => startSync(c),
      })
    }
  }, [])

  // Initialize client asynchronously (IndexedDB store needs async startup)
  useEffect(() => {
    let cancelled = false
    setStatus(MatrixConnectionStatus.Connecting)

    createMatrixClient(credentials)
      .then(newClient => {
        if (cancelled) return
        clientRef.current = newClient
        setClient(newClient)
      })
      .catch(e => {
        if (cancelled) return
        logger.error('Failed to create Matrix client', {safeMessage: e})
        setStatus(MatrixConnectionStatus.Error)
        setError({
          code: MatrixErrorCode.InitFailed,
          exception: e instanceof Error ? e : new Error(String(e)),
          retry: () => {
            // Re-trigger the effect by toggling state
            setClient(null)
            setStatus(MatrixConnectionStatus.Connecting)
          },
        })
      })

    return () => {
      cancelled = true
      if (clientRef.current) {
        clientRef.current.stopClient()
        clientRef.current = null
      }
    }
  }, [credentials.matrixUserId]) // eslint-disable-line react-hooks/exhaustive-deps

  // Start sync once client is ready
  useEffect(() => {
    if (!client) return

    startSync(client)

    const onSync = (state: string) => {
      if (state === 'PREPARED' || state === 'SYNCING') {
        setStatus(MatrixConnectionStatus.Ready)
        setError(null)

        if (state === 'PREPARED') {
          pruneStaleRooms(client)
        }
      } else if (state === 'ERROR' || state === 'RECONNECTING') {
        setStatus(MatrixConnectionStatus.Error)
        setError({
          code: MatrixErrorCode.SyncFailed,
          retry: () => startSync(client),
        })
      } else if (state === 'STOPPED') {
        // Only set error if we didn't intentionally stop (e.g. backgrounding)
        if (appStateRef.current === 'active') {
          setStatus(MatrixConnectionStatus.Error)
          setError({
            code: MatrixErrorCode.SyncFailed,
            retry: () => startSync(client),
          })
        }
      }
    }
    client.on('sync' as any, onSync)

    // Periodically prune stale rooms to catch mid-session backend deletions
    pruneIntervalRef.current = setInterval(() => {
      pruneStaleRooms(client)
    }, 60_000)

    return () => {
      client.removeListener('sync' as any, onSync)
      if (pruneIntervalRef.current) {
        clearInterval(pruneIntervalRef.current)
        pruneIntervalRef.current = null
      }
      client.stopClient()
      setStatus(MatrixConnectionStatus.Uninitialized)
    }
  }, [client, startSync])

  // Foreground/background lifecycle
  useEffect(() => {
    const subscription = AppState.addEventListener(
      'change',
      (nextState: AppStateStatus) => {
        const c = clientRef.current
        if (!c) return

        if (appStateRef.current !== 'active' && nextState === 'active') {
          // Resuming from background
          const wasBackgroundedAt = backgroundedAtRef.current
          backgroundedAtRef.current = null

          if (
            wasBackgroundedAt &&
            Date.now() - wasBackgroundedAt > INACTIVE_TIMEOUT
          ) {
            // Suspended: long background, need full re-sync
            logger.info('Matrix: resuming from suspended state, full re-sync')
            setStatus(MatrixConnectionStatus.Connecting)
            c.stopClient()
            startSync(c)
          } else {
            // Quick resume
            startSync(c)
          }
        } else if (appStateRef.current === 'active' && nextState !== 'active') {
          // Going to background
          backgroundedAtRef.current = Date.now()
          c.stopClient()
          setStatus(MatrixConnectionStatus.Backgrounded)
        }
        appStateRef.current = nextState
      },
    )
    return () => subscription.remove()
  }, [startSync])

  // Auto-retry recoverable pending messages when connection is restored
  useEffect(() => {
    if (status === MatrixConnectionStatus.Ready) {
      pendingMessageStore.retryRecoverable()
    }
  }, [status])

  // Derive legacy booleans for backward compatibility
  const isConnected = isMatrixReady(status)
  const isSyncing = status !== MatrixConnectionStatus.Uninitialized

  return (
    <MatrixContext.Provider
      value={{client, status, error, isConnected, isSyncing}}>
      {children}
    </MatrixContext.Provider>
  )
}

async function pruneStaleRooms(client: MatrixClient) {
  try {
    const {joined_rooms: serverRoomIds} = await client.getJoinedRooms()
    const serverSet = new Set(serverRoomIds)
    const cachedRooms = client.getRooms()
    let pruned = false

    for (const room of cachedRooms) {
      if (room.getMyMembership() === 'join' && !serverSet.has(room.roomId)) {
        logger.debug('Pruning stale cached room', {roomId: room.roomId})
        pruned = true
        try {
          await client.forget(room.roomId)
        } catch {
          // Room may already be deleted server-side; remove from local store directly
          const store = client.store
          if (store?.removeRoom) {
            store.removeRoom(room.roomId)
          }
        }
      }
    }

    // Notify listeners so the room list refreshes
    if (pruned) {
      client.emit('Room' as any)
    }
  } catch (e) {
    logger.warn('Failed to prune stale rooms', {safeMessage: e})
  }
}
